import {useState, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import Modal from "../feedBack/Modal";
import {useStateContext} from "../../contexts/ContextProvider";
import {useFacilityWithChildren, Facility, FacilityWithChildren} from "../../pages/settings/facilities/useFacility";
import {BuildingOffice2Icon, BuildingOfficeIcon, CheckCircleIcon, MagnifyingGlassIcon} from "@heroicons/react/24/solid";
import Loader from "../dataDisplay/Loader";
import SuperAdminFacilitySwitcherModal from "./SuperAdminFacilitySwitcherModal";

interface FacilitySwitcherModalProps {
    open: boolean;
    setOpen: (value: boolean) => void;
}

/**
 * Aiguillage : un super admin cherche parmi tous les établissements, un admin
 * de groupe choisit parmi les enfants de son groupe.
 */
export default function FacilitySwitcherModal({open, setOpen}: FacilitySwitcherModalProps) {
    const {user} = useStateContext();

    return user.is_super_admin
        ? <SuperAdminFacilitySwitcherModal open={open} setOpen={setOpen}/>
        : <GroupFacilitySwitcherModal open={open} setOpen={setOpen}/>;
}

function GroupFacilitySwitcherModal({open, setOpen}: FacilitySwitcherModalProps) {
    const navigate = useNavigate();
    const {currentFacility, setCurrentFacility, user} = useStateContext();
    const {t} = useTranslation(['navigation', 'common']);
    const [search, setSearch] = useState("");

    // Seuls les admins de groupe peuvent utiliser ce modal
    // On récupère le groupe de l'utilisateur (son facility d'origine qui est un groupe)
    const groupFacilityId = user?.facility?.is_group ? user.facility.id : null;

    // Récupérer le groupe avec ses établissements enfants
    const {data: groupFacility, isLoading} = useFacilityWithChildren(groupFacilityId);

    const handleSelectFacility = (facility: Facility) => {
        const isChangingFacility = currentFacility?.id !== facility.id;

        // Pour les groupes, child_facilities (du endpoint with-children) doit être
        // mappé vers facilities (attendu par le reste de l'app via currentFacility)
        const facilityWithChildren = facility as FacilityWithChildren;
        if (facilityWithChildren.child_facilities && !facility.facilities) {
            facility = {...facility, facilities: facilityWithChildren.child_facilities};
        }

        setCurrentFacility(facility);
        setOpen(false);
        setSearch("");

        // Rediriger vers /settings/facilities après un changement d'établissement
        if (isChangingFacility) {
            navigate('/settings/facilities');
        }
    };

    // Liste de toutes les facilities disponibles (groupe + établissements enfants)
    const allFacilities = groupFacility
        ? [groupFacility, ...(groupFacility.child_facilities || [])]
        : [];

    // Filtrer les facilities selon la recherche
    const filteredFacilities = useMemo(() => {
        if (!search.trim()) return allFacilities;
        const searchLower = search.toLowerCase().trim();
        return allFacilities.filter(facility =>
            facility.name?.toLowerCase().includes(searchLower) ||
            facility.city?.toLowerCase().includes(searchLower)
        );
    }, [allFacilities, search]);

    return (
        <Modal
            open={open}
            setOpen={setOpen}
            title={t('navigation:facility_switcher.title')}
            size="md"
            showFooter={false}
        >
            {isLoading ? (
                <div className="flex justify-center py-6">
                    <Loader message={t('common:states.loading')}/>
                </div>
            ) : (
                <div>
                    {/* Barre de recherche */}
                    {allFacilities.length > 5 && (
                        <div className="relative mb-3">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"/>
                            <input
                                type="text"
                                placeholder={t('navigation:facility_switcher.search_placeholder')}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-color focus:border-transparent"
                            />
                        </div>
                    )}

                    <div className="max-h-[350px] overflow-y-auto -mx-4 px-4">
                        {filteredFacilities.length === 0 ? (
                            <p className="text-center text-gray-500 py-6 text-sm">
                                {search ? t('navigation:facility_switcher.no_results') : t('navigation:facility_switcher.no_facilities')}
                            </p>
                        ) : (
                            <div className="space-y-2">
                                {filteredFacilities.map((facility) => {
                                const isActive = currentFacility?.id === facility.id;
                                const isGroup = facility.is_group;

                                return (
                                    <button
                                        key={facility.id}
                                        onClick={() => handleSelectFacility(facility)}
                                        className={`w-full text-left bg-white px-3 py-2.5 rounded-lg transition-all duration-150 flex items-center gap-3 border ${
                                            isActive
                                                ? 'border-main-color'
                                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        {/* Icône */}
                                        <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                                            isGroup
                                                ? (isActive ? 'bg-primary-100' : 'bg-purple-100')
                                                : (isActive ? 'bg-primary-100' : 'bg-gray-100')
                                        }`}>
                                            {isGroup ? (
                                                <BuildingOffice2Icon className={`h-4 w-4 ${isActive ? 'text-main-color' : 'text-purple-600'}`}/>
                                            ) : (
                                                <BuildingOfficeIcon className={`h-4 w-4 ${isActive ? 'text-main-color' : 'text-gray-600'}`}/>
                                            )}
                                        </div>

                                        {/* Nom */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className={`text-sm truncate ${isActive ? 'font-semibold text-main-color' : 'font-medium text-gray-900'}`}>
                                                    {facility.name}
                                                </span>
                                                {isGroup ? (
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-medium">
                                                        {t('navigation:facility_switcher.group_badge')}
                                                    </span>
                                                ):null}
                                            </div>
                                            {facility.city ? (
                                                <p className="text-xs text-gray-500 truncate">
                                                    {facility.city}
                                                </p>
                                            ):null}
                                        </div>

                                        {/* Indicateur actif */}
                                        {isActive && (
                                            <CheckCircleIcon className="h-5 w-5 text-main-color flex-shrink-0"/>
                                        )}
                                    </button>
                                );
                            })}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Modal>
    );
}
